import useAuth from '@/hooks/useAuth'
import { useClientRect } from '@/hooks/useClientRect'
import { createGuestHeaders } from '@/utils/createGuestHeaders'
import { Box, Typography, Card, Grid, LinearProgress, makeStyles, useMediaQuery } from '@material-ui/core'
import SunIcon from '@material-ui/icons/WbSunny'
import MoonIcon from '@material-ui/icons/NightsStay'
import { memo, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import api from '@micmnt/apis'
import clsx from 'clsx'
import PeriodSelection from '@/components/PeriodSelection'
import InfoCard from '@/components/InfoCard'
import { Skeleton } from '@material-ui/lab'
import DownloadExcelButton from '../../shared/DownloadExcelButton'
import ShareButton from '@/components/ShareButton'
import { decodeCardsFromApi, generateChartSerie, getDatesFromPeriod, getDisabledSeries } from '../utils'
import TableView from '@/components/TableView'
import { formatLabels } from '@/components/charts/SVGCharts/utils'
import { format, differenceInWeeks } from 'date-fns'
import LineAndBarChart from '@/components/charts/SVGCharts/LineAndBarChart'
import { europeNum } from '@/utils/general'
import ModalAssetsLogs from '@/components/ModalAssetsLogs'
import { basicColors } from '@/theme'
import moment from 'moment'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    paddingRight: theme.spacing(2)
  },
  section: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%'
  },
  card: {
    padding: theme.spacing(2),
    width: '100%'
  },
  sectionTitle: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main
  },
  topMargin: {
    marginTop: (theme.spacing(2) - 4)
  },
  label: {
    textTransform: 'uppercase',
    color: basicColors.blueGrayLight
  },
  value: {
    fontSize: '24px'
  },
  valueBig: {
    fontSize: '42px'
  },
  smallViewport: {
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%'
  },
  glassBackground: {
    '@supports (backdrop-filter: none) or (-webkit-backdrop-filter: none)': {
      '-webkit-backdrop-filter': 'saturate(120%) blur(16px)',
      backdropFilter: 'saturate(190%) blur(16px)',
      backgroundColor: 'rgba(16, 26, 38, 0.05)'
    }
  }
}))

const Production = ({ hasGlass = false, period = null, shared = false, guestToken = null, plantId: sharedPlantId = null, className, ...rest }) => {
  const classes = useStyles()
  const { user, currentProject } = useAuth()

  // Variabile che rappresenta il nome dell'impianto
  const { uuid: plantUuid, name: plantName, plantType } = useSelector(state => state.publicEnergyPlantView)
  // Variabile di stato che rappresenta lo stato di caricamento dei dati
  const [isLoading, setIsLoading] = useState(false)
  // periodo selezionato tramite le chips
  const [customPeriod, setCustomPeriod] = useState('live')
  // data/intervallo di tempo selezionato tramite il date picker
  const [selectedDate, setSelectedDate] = useState(null)
  // periodo impostato dallo zoom sul grafico
  const [zoomPeriod, setZoomPeriod] = useState('live')
  // insieme delle serie da graficare
  const [chartData, setChartData] = useState({})
  // dati delle cards
  const [cardsData, setCardsData] = useState([])
  // assets dell'impianto
  const [plantAssets, setPlantAssets] = useState([])
  // devices dell'impianto
  const [plantDevices, setPlantDevices] = useState([])
  // oggetto con orari di alba e tramonto
  const [suntimes, setSuntimes] = useState({ sunrise: null, sunset: null })
  // Boolean per la visualizzazione della modal
  const [selectedAsset, setSelectedAsset] = useState(null)

  const plantId = sharedPlantId || plantUuid

  // Ref per calcolare l'altezza dei grafici
  const [rect, ref] = useClientRect()

  useEffect(() => {
    if (period) {
      setCustomPeriod(period)
    }
  }, [period])

  // Funzione per fare la GET dei dati
  const getData = useCallback(async (timeFrom, timeTo) => {
    const customHeaders = createGuestHeaders(guestToken)
    if (plantId) {
      const { data: response, error } = await api.get({
        savedUrl: 'plantDetails',
        fullResponse: true,
        path: `/${plantId}/production?timeFrom=${timeFrom}&timeTo=${timeTo}`,
        customHeaders
      })

      if (error) {
        return null
      }

      const { data: assets, error: assetsError } = await api.get({
        savedUrl: `https://api.apio.network/projects/${currentProject?.uuid}/assets`,
        params: { filter: { plantId } }
      })

      if (assetsError) {
        return null
      }

      const { data: devices, error: devicesError } = await api.get({
        savedUrl: `https://api.apio.network/projects/${currentProject?.uuid}/devices`,
        params: { filter: { plantId } }
      })

      if (devicesError) {
        return null
      }

      const dataObj = response.data
      return {
        dataObj,
        assets,
        devices
      }
    }
  }, [plantId, guestToken])

  const params = [{ name: 'plantId', value: plantId }, { name: 'plantName', value: plantName }, { name: 'plantType', value: plantType }]

  useEffect(() => {
    // Funzione che fa l'inizializzazione della view, settando i dati che i grafici consumeranno
    async function initialiseView (timeFrom, timeTo) {
      setIsLoading(true)
      const { dataObj, assets, devices } = await getData(timeFrom, timeTo)
      if (dataObj) {
        const { cumulated, energy, power, suntimes } = dataObj

        const { sunrise, sunset } = suntimes

        const sunriseDate = moment(sunrise).format('HH:mm')
        const sunsetDate = moment(sunset).format('HH:mm')

        setSuntimes({ sunrise: sunriseDate, sunset: sunsetDate })

        setCardsData(decodeCardsFromApi({ exportedEnergy: cumulated.total }))

        if (differenceInWeeks(new Date(timeTo), new Date(timeFrom)) < 1) {
          setChartData({
            energy,
            power
          })
        } else {
          setChartData({
            energy
          })
        }
      }

      if (assets) {
        setPlantAssets(assets)
      }

      if (devices) {
        setPlantDevices(devices)
      }
      setIsLoading(false)
    }
    const periodDate = (selectedDate && selectedDate.toDate()) || new Date()
    // Prendo le date minima e massima da passare alle api
    const { minDate, maxDate } = getDatesFromPeriod(customPeriod, periodDate)
    if (!shared) {
      if (customPeriod === 'live') {
        if (zoomPeriod === 'live') {
          // Prima GET
          initialiseView(minDate, maxDate)
          // Attivazione del polling
          const pollingInterval = setInterval(() => {
            initialiseView(minDate, maxDate)
          }, 60000)
          return () => {
            clearInterval(pollingInterval)
          }
        }
      } else {
        if (customPeriod === zoomPeriod) {
          initialiseView(minDate, maxDate)
        }
      }
    } else {
      initialiseView(minDate, maxDate)
    }
  }, [customPeriod, plantId, getData, selectedDate, zoomPeriod, shared])

  const isExtraSmall = useMediaQuery(theme => theme.breakpoints.down('xs'))
  const isLarge = useMediaQuery(theme => theme.breakpoints.down('lg'))

  const graphHeight = isLarge ? rect?.height - 350 : rect ? rect.height - 330 : '100%'
  const graphWidth = rect?.width

  const periodFormatting = (period = 'live') => {
    if (period === 'week' || period === 'month') {
      return 'dd/MM/yy'
    } else if (period === 'year') {
      return 'MMMM'
    }

    return 'HH:mm'
  }

  const chartLabels = formatLabels(chartData?.energy?.map(el => el.label) || [], (label) => format(new Date(label), periodFormatting(customPeriod)))

  const seriesChartConfig = {
    power: {
      name: 'Potenza',
      color: 'blue',
      uom: 'kW',
      format: (value) => europeNum(value, 2)
    },
    energy: {
      name: 'Energia',
      color: 'orange',
      type: 'bar',
      uom: 'kWh',
      format: (value) => europeNum(value, 2)
    }
  }

  const tableHeader = [
    { value: 'name', label: 'Quadro' },
    { value: 'state.power', label: 'Potenza (kW)', formatter: (num) => europeNum(num) },
    { value: 'propertyMappings.power.resourceId', label: 'Dispositivo associato', formatter: (resourceId) => plantDevices?.find(device => device.uuid === resourceId)?.name ?? '-' },
    { value: 'stateUpdatedAt.power', label: 'Last mex.', formatter: (isoDate) => format(new Date(isoDate), 'dd/MM/yy HH:mm') }
  ]

  const tableRowAction = (data) => {
    if (data) {
      setSelectedAsset(data)
    }
  }

  const roundNumberFormatter = (value) => {
    // Converto il valore da string a number
    const numberValue = Number(value?.replaceAll('.', ''))

    // Se il numero è maggiore di 1M, divido per 1M e ritorno numberM
    if (numberValue > 1000000) {
      return `${europeNum(numberValue / 1000000, 0)}M`
      // Altrimenti se è maggiore di 1K, divido per 1k e ritorno numberK
    } else if (numberValue > 1000) {
      return `${europeNum(numberValue / 1000, 0)}K`
      // Atrimenti ritorno il numero
    } else {
      return value
    }
  }

  const electricPanelsNumber = plantAssets?.filter(asset => asset.assetType?.name === 'Quadro Elettrico')?.length ?? '-'

  return (
    <div
      className={
        isLarge
          ? clsx(classes.root, className, classes.smallViewport)
          : clsx(classes.root, className)
      }
      {...rest}
    >
      {selectedAsset ? <ModalAssetsLogs plantType={plantType} selectedAsset={selectedAsset} devices={plantDevices} open={!!selectedAsset} onClose={() => setSelectedAsset(null)} /> : null}
      <Box mt={2}>
        <Grid width='100%' container spacing={1}>
          {shared
            ? null
            : (
              <Grid style={{ order: 1 }} item xs={12} sm={10} md={10} xl={3}>
                <PeriodSelection
                  isLoading={isLoading}
                  selectedDate={selectedDate}
                  setSelectedDate={(date) => {
                    setZoomPeriod(customPeriod)
                    setSelectedDate(date)
                  }}
                  customPeriod={customPeriod}
                  setCustomPeriod={(period) => {
                    setCustomPeriod(period)
                    setZoomPeriod(period)
                  }}
                />
              </Grid>)}
          <Grid style={{ order: isLarge ? 3 : 2 }} item xs={12} md={12} xl={shared ? 12 : 8}>
            <Box width='100%'>
              <Grid container spacing={1}>
                {
                  cardsData.length > 0
                    ? cardsData.map((card, index) => (
                      <Grid key={`card-${index}`} item xs={12} md={6} lg={3}>
                        <InfoCard elevation={hasGlass ? 0 : 1} className={hasGlass ? classes.glassBackground : null} icon={card.icon} values={card.values} />
                      </Grid>))
                    : (
                      <>
                        <Grid item xs={12} md={6} lg={3}>
                          <Skeleton variant='rect' height={60} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Skeleton variant='rect' height={60} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Skeleton variant='rect' height={60} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Skeleton variant='rect' height={60} />
                        </Grid>
                      </>)
                }
              </Grid>
            </Box>
          </Grid>
          {shared
            ? null
            : !isExtraSmall && (
              <Grid style={{ order: isLarge ? 2 : 3 }} item xs={2} sm={2} md={2} xl={1}>
                <Box width='100%' height='100%' display='flex' alignItems='center' justifyContent='flex-end'>
                  <Box mr={1}>
                    <DownloadExcelButton
                      selectedDate={selectedDate}
                      period={customPeriod}
                      plantId={plantId}
                      plantName={plantName}
                      exportType='production'
                      description='Scarica i dati di produzione del periodo che stai visualizzando in un file Excel'
                    />
                  </Box>
                  <ShareButton userEmail={user ? user.email : null} userId={user ? user.uuid : null} path='publicIllumination' params={params} period={customPeriod} />
                </Box>
              </Grid>)}
        </Grid>
      </Box>
      <Box ref={ref} width='100%' height='90%' mt={1}>
        <Grid container spacing={1}>
          <Grid item xs={10}>
            <Card elevation={hasGlass ? 0 : 1} className={hasGlass ? clsx(classes.section, classes.glassBackground) : classes.section}>
              {isLoading
                ? (
                  <Box mt={-2} ml={-2}>
                    <LinearProgress color='primary' />
                  </Box>)
                : null}
              {shared
                ? <Typography className={isLoading ? clsx(classes.topMargin, classes.sectionTitle) : classes.sectionTitle} variant='h6'>Potenza ed Energia</Typography>
                : null}
              <Box width='100%' display='flex' alignItems='center' justifyContent='center'>
                <LineAndBarChart
                  barWidth={20}
                  width={graphWidth}
                  height={graphHeight * 0.6}
                  initialHiddenSeries={getDisabledSeries(seriesChartConfig)}
                  data={{
                    series: Object.keys(chartData).map(key => generateChartSerie(chartData[key], seriesChartConfig[key], (el) => roundNumberFormatter(el))),
                    labels: chartLabels
                  }}
                />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={2}>
            <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }} gridGap={8}>
              <Card style={{ flex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} elevation={hasGlass ? 0 : 1} className={hasGlass ? clsx(classes.card, classes.glassBackground) : classes.card}>
                <Box>
                  <Typography className={classes.label} component='div' variant='caption'>Luogo</Typography>
                  <Typography className={classes.value} component='div' variant='h3'>Termoli</Typography>
                </Box>
                <Box>
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Box display='flex' alignItems='center' justifyContent='center' gridGap={8}>
                      <SunIcon fontSize='small' htmlColor={basicColors.blueGrayLight} />
                      <Typography className={classes.label} component='div' variant='caption'>Alba</Typography>
                    </Box>
                    <Typography className={classes.value} component='div' variant='h3'>{suntimes?.sunrise ? suntimes?.sunrise : '--:--'}</Typography>
                  </Box>
                  <Box display='flex' alignItems='center' justifyContent='space-between' mt={2}>
                    <Box display='flex' alignItems='center' justifyContent='center' gridGap={8}>
                      <MoonIcon fontSize='small' htmlColor={basicColors.blueGrayLight} />
                      <Typography className={classes.label} component='div' variant='caption'>Tramonto</Typography>
                    </Box>
                    <Typography className={classes.value} component='div' variant='h3'>{suntimes?.sunset ? suntimes?.sunset : '--:--'}</Typography>
                  </Box>
                </Box>
              </Card>
              <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} elevation={hasGlass ? 0 : 1} className={hasGlass ? clsx(classes.card, classes.glassBackground) : classes.card}>
                <Typography className={classes.label} component='div' variant='caption'>N. Quadri</Typography>
                <Typography className={classes.valueBig} component='div' variant='h3'>{electricPanelsNumber}</Typography>
              </Card>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Card elevation={hasGlass ? 0 : 1} className={hasGlass ? clsx(classes.section, classes.glassBackground) : classes.section}>
              <TableView header={tableHeader} data={plantAssets} rowAction={shared ? null : tableRowAction} />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}

export default memo(Production)
